import { cn } from '@morpho-org/ui-v6/utils'
import { formatters } from '@morpho-org/ui-v6/utils'
import { forwardRef, type HTMLAttributes } from 'react'

export interface CellLLTVProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Liquidation Loan-To-Value ratio as bigint (18 decimals) */
  lltv: bigint
}

/**
 * CellLLTV component - Tailwind replica of dapp-ui-v5 CellLLTV
 *
 * Displays a formatted LLTV (Liquidation Loan-To-Value) percentage.
 *
 * Matches v5 CellLLTV behavior:
 * - Formats LLTV as percentage with 2 decimal places
 * - Shows "-" for undefined/zero values
 * - Typography: body xxs regular
 * - Color: text body
 *
 * @example
 * ```tsx
 * // Display 80% LLTV
 * <CellLLTV lltv={800000000000000000n} />
 *
 * // Display 90% LLTV
 * <CellLLTV lltv={900000000000000000n} />
 * ```
 */
const CellLLTV = forwardRef<HTMLSpanElement, CellLLTVProps>(
  ({ lltv, className, ...props }, ref) => {
    const formattedLltv = formatters.percent.digits(2).unit('%').default('-').of(BigInt(lltv), 18)

    return (
      <span ref={ref} className={cn('text-2xs text-foreground', className)} {...props}>
        {formattedLltv}
      </span>
    )
  }
)

CellLLTV.displayName = 'CellLLTV'

export { CellLLTV }
